/**
 * Microsoft's Message Center feed — the announced-change side of this board.
 *
 * The backend has parsed and served this since the feed was switched on; until
 * now nothing rendered it, so the only Message Center signal on the board was
 * an alert saying the permission was missing. Nothing new is fetched here: the
 * posts arrive inside the `/m365/service-health` payload the board already
 * reads.
 *
 * ## Why deadlines lead
 *
 * Most posts are informational. A few carry `action_required_by_date_time` — a
 * date by which the tenant has to have done something, after which Microsoft
 * proceeds regardless and there is no second notice. That is the actual
 * operational pain, so those posts come first, soonest deadline first, and an
 * already-passed deadline is louder still rather than being quietly sorted to
 * the bottom of a "past" section.
 *
 * ## Three states, not two
 *
 * `messages: []` is ambiguous on its own and this panel never treats it as one
 * answer:
 *
 * 1. **Unreadable** — `messages_available` is false, so Graph refused the read
 *    and `missing_scopes` names the permission. Nothing is known about what
 *    Microsoft has posted.
 * 2. **Empty** — the read worked and Microsoft has posted nothing in the window
 *    the backend keeps.
 * 3. **Populated** — posts, led by whatever has a deadline.
 *
 * The board-level `errors[]` alert (`M365ErrorsAlert`) crosses state 2: a
 * refresh whose Message Center read failed *after* being permitted also lands
 * on an empty list, so the empty copy points at that notice instead of
 * asserting a quiet month.
 *
 * ## Altitude (L1)
 *
 * The deadline list renders in full up to `MAX_DEADLINE_ROWS` because a hidden
 * deadline is the failure mode; the remaining posts are a capped feed with the
 * shortfall stated, not a 100-row table. Microsoft's own admin center is the L2
 * for the archive, which is why nothing here pretends to be a complete list.
 */

import { CalendarClock, Icon, Megaphone } from "@norican/icons";
import { Alert, Badge, Card, EmptyState, Timeline, Typography } from "@norican/react";

import type { M365Message } from "@/lib/api";

import {
  DEADLINE_TONE,
  deadlineUrgency,
  formatDateTime,
  formatDeadline,
  messageSeverityTone,
} from "./status";

/**
 * Deadline rows rendered before the list is capped. Deliberately generous — a
 * deadline that is on the board but scrolled past is still actionable, a
 * deadline that is not on the board at all is not.
 */
const MAX_DEADLINE_ROWS = 10;
/** Rows of the remaining feed. These are notices, so the cap is tighter. */
const MAX_ANNOUNCEMENT_ROWS = 6;

/** A post Microsoft has attached a deadline to, with that deadline's urgency. */
interface DeadlinedMessage {
  message: M365Message;
  urgency: "overdue" | "soon" | "later";
}

interface MessagesPanelProps {
  /** Posts from the snapshot, in the backend's newest-modified-first order. */
  messages: M365Message[];
  /** `messages_available` — false means Graph refused the read entirely. */
  available: boolean;
  /** Graph permissions the backend reports as ungranted. */
  missingScopes: string[];
  /**
   * Whether the snapshot carried `errors[]`. Not rendered here — the board
   * renders those once, above — but it changes what an empty feed is allowed to
   * claim, because a failed read and a quiet month both arrive as `[]`.
   */
  hasReadErrors: boolean;
}

export function MessagesPanel({
  messages,
  available,
  missingScopes,
  hasReadErrors,
}: MessagesPanelProps) {
  // State 1. No heading, no empty panel: nothing is known, so the alert is the
  // whole answer — the same shape `M365SetupPanel` takes for the board.
  if (!available) {
    return (
      <Alert tone="neutral" title="Message Center could not be read">
        {missingScopes.length > 0
          ? `Microsoft Graph refused this feed: NOVA is not granted ${missingScopes.join(", ")}. `
          : "Microsoft Graph refused this feed. "}
        Announced changes, planned maintenance and any deadlines Microsoft has set for this tenant
        are unknown to NOVA — not absent. Service health above is unaffected.
      </Alert>
    );
  }

  // Urgency is resolved once per post and carried alongside it: it decides both
  // which list a post belongs in and how loud its marker is, and re-deriving it
  // at render time is how those two drift apart.
  const graded = messages.map((message) => ({
    message,
    urgency: deadlineUrgency(message.action_required_by_date_time),
  }));
  const dated = graded
    .filter((entry): entry is DeadlinedMessage => entry.urgency !== null)
    .sort(bySoonestDeadline);
  const undated = graded
    .filter((entry) => entry.urgency === null)
    .map((entry) => entry.message)
    .sort(byMajorChangeThenRecency);
  const hiddenDeadlines = Math.max(0, dated.length - MAX_DEADLINE_ROWS);
  const hiddenAnnouncements = Math.max(0, undated.length - MAX_ANNOUNCEMENT_ROWS);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-4">
        <Typography variant="heading-4" as="h2">
          Message Center
        </Typography>
        <Typography variant="body-sm" tone="muted">
          {messages.length} {messages.length === 1 ? "post" : "posts"} ·{" "}
          {dated.length === 0 ? "none need action by a date" : `${dated.length} with a deadline`}
        </Typography>
      </div>

      {/* State 2. One empty state for one fact, rather than an empty deadline
          card sitting above an empty feed card. */}
      {messages.length === 0 ? (
        <Card variant="outline">
          <Card.Body>
            <EmptyState
              size="sm"
              title="No Message Center posts"
              description={
                hasReadErrors
                  ? "Microsoft Graph answered with no posts — but part of this refresh could not be read (see the notice above), so an unreadable post would also land here."
                  : "The read succeeded and Microsoft has announced nothing for this tenant. This is an empty feed, not an unavailable one."
              }
            />
          </Card.Body>
        </Card>
      ) : (
        <>
          {/* State 3, the part that matters: deadlines, soonest first. */}
          <Card
            variant="outline"
            accent="left"
            accentTone={dated.length > 0 ? deadlineCardTone(dated) : "neutral"}
          >
            <Card.Header>
              <div className="flex items-baseline justify-between gap-4">
                <Typography variant="heading-6" as="h3">
                  Action needed by a date
                </Typography>
                <Typography variant="body-sm" tone="muted">
                  Soonest first, overdue at the top
                </Typography>
              </div>
            </Card.Header>
            <Card.Body>
              {dated.length === 0 ? (
                <EmptyState
                  size="sm"
                  title="Nothing is due"
                  description="No current post asks this tenant to act by a date. Microsoft sets these deadlines itself, so an empty list here can change with the next refresh."
                />
              ) : (
                <div className="flex flex-col gap-3">
                  <Timeline
                    orientation="vertical"
                    items={dated.slice(0, MAX_DEADLINE_ROWS).map(({ message, urgency }) => ({
                      id: message.id,
                      tone: DEADLINE_TONE[urgency],
                      icon: <Icon icon={CalendarClock} size="sm" />,
                      timestamp: formatDeadline(message.action_required_by_date_time),
                      title: <MessageTitle message={message} />,
                      description: (
                        <MessageDetail
                          message={message}
                          trailing={`due ${formatDateTime(message.action_required_by_date_time)}`}
                        />
                      ),
                    }))}
                  />
                  {hiddenDeadlines > 0 && (
                    <Typography variant="caption" as="p">
                      {hiddenDeadlines} further post{hiddenDeadlines === 1 ? "" : "s"} carr
                      {hiddenDeadlines === 1 ? "ies" : "y"} a later deadline and{" "}
                      {hiddenDeadlines === 1 ? "is" : "are"} not shown. The Microsoft 365 admin
                      center has the full feed.
                    </Typography>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>

          {/* The rest of the feed — awareness, not a to-do list. */}
          {undated.length > 0 && (
            <Card variant="outline">
              <Card.Header>
                <div className="flex items-baseline justify-between gap-4">
                  <Typography variant="heading-6" as="h3">
                    Announced changes
                  </Typography>
                  <Typography variant="body-sm" tone="muted">
                    Major changes first, then most recently updated
                  </Typography>
                </div>
              </Card.Header>
              <Card.Body>
                <div className="flex flex-col gap-3">
                  <Timeline
                    orientation="vertical"
                    items={undated.slice(0, MAX_ANNOUNCEMENT_ROWS).map((message) => ({
                      id: message.id,
                      tone: message.is_major_change ? ("warning" as const) : ("default" as const),
                      icon: <Icon icon={Megaphone} size="sm" />,
                      timestamp: formatDateTime(message.last_modified_date_time),
                      title: <MessageTitle message={message} />,
                      description: <MessageDetail message={message} />,
                    }))}
                  />
                  {hiddenAnnouncements > 0 && (
                    <Typography variant="caption" as="p">
                      {hiddenAnnouncements} more post{hiddenAnnouncements === 1 ? "" : "s"} with no
                      deadline {hiddenAnnouncements === 1 ? "is" : "are"} not shown — this panel is
                      a summary of the feed, not the archive.
                    </Typography>
                  )}
                </div>
              </Card.Body>
            </Card>
          )}
        </>
      )}
    </section>
  );
}

interface MessageRowProps {
  message: M365Message;
}

/** Headline, post id, and the two flags an operator triages on. */
function MessageTitle({ message }: MessageRowProps) {
  return (
    <span className="flex flex-wrap items-center gap-2">
      <span className="text-fg font-medium">{message.title}</span>
      <Badge tone="neutral" variant="outline" size="xs">
        {message.id}
      </Badge>
      {/* Severity is Microsoft's own label, so it is printed rather than
          re-worded — and it is a label, not just a colour (§3.2). */}
      <Badge tone={messageSeverityTone(message.severity)} variant="subtle" size="xs">
        {message.severity_label}
      </Badge>
      {message.is_major_change && (
        <Badge tone="warning" variant="outline" size="xs">
          Major change
        </Badge>
      )}
    </span>
  );
}

interface MessageDetailProps extends MessageRowProps {
  /** Extra clause appended to the meta line, e.g. the absolute due date. */
  trailing?: string;
}

/** Which services a post touches, then its summary — clamped, never HTML. */
function MessageDetail({ message, trailing }: MessageDetailProps) {
  return (
    <span className="flex flex-col gap-0.5">
      <span className="text-fg-muted text-xs">
        {[
          message.category_label,
          // An unnamed service is stated as unnamed. Guessing "all of Microsoft
          // 365" from an empty array would be inventing scope.
          message.services.length > 0
            ? message.services.join(", ")
            : "No affected service named by Microsoft",
          trailing,
        ]
          .filter(Boolean)
          .join(" · ")}
      </span>
      {message.summary && (
        // Stripped to plain text by the backend, so this is ordinary text —
        // there is deliberately no `dangerouslySetInnerHTML` in this feature.
        <span className="text-fg-muted line-clamp-2 text-xs">{message.summary}</span>
      )}
    </span>
  );
}

/** Overdue first, then soonest — ascending deadline does both at once. */
function bySoonestDeadline(a: DeadlinedMessage, b: DeadlinedMessage): number {
  const left = new Date(a.message.action_required_by_date_time ?? 0).getTime();
  const right = new Date(b.message.action_required_by_date_time ?? 0).getTime();
  return left - right;
}

/** Major changes lead the deadline-free feed; recency breaks the tie. */
function byMajorChangeThenRecency(a: M365Message, b: M365Message): number {
  if (a.is_major_change !== b.is_major_change) {
    return a.is_major_change ? -1 : 1;
  }
  const left = new Date(a.last_modified_date_time ?? 0).getTime();
  const right = new Date(b.last_modified_date_time ?? 0).getTime();
  return right - left;
}

/**
 * The card's accent takes the urgency of its most urgent row — which, because
 * the list is sorted ascending, is the first one.
 */
function deadlineCardTone(dated: DeadlinedMessage[]): "danger" | "warning" | "neutral" {
  const worst = dated[0]?.urgency;
  if (worst === "overdue") {
    return "danger";
  }
  return worst === "soon" ? "warning" : "neutral";
}
